import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCompanyInvitations, getAddedUsersOfCompany, sendInviteAndUpdate, updateOtherUsers } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { userTypeOf } from '../../util/userType';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/UserManagementPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/UserManagementPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/UserManagementPage/FETCH_ORDERS_OR_SALES_ERROR';

export const SEND_INVITE_REQUEST = 'app/UserManagementPage/SEND_INVITE_REQUEST';
export const SEND_INVITE_SUCCESS = 'app/UserManagementPage/SEND_INVITE_SUCCESS';
export const SEND_INVITE_ERROR = 'app/UserManagementPage/SEND_INVITE_ERROR';

export const FETCH_ALL_USERS_REQUEST = 'app/UserManagementPage/FETCH_ALL_USERS_REQUEST';
export const FETCH_ALL_USERS_SUCCESS = 'app/UserManagementPage/FETCH_ALL_USERS_SUCCESS';
export const FETCH_ALL_USERS_ERROR = 'app/UserManagementPage/FETCH_ALL_USERS_ERROR';

export const UPDATE_ADDED_USER_REQUEST = 'app/UserManagementPage/UPDATE_ADDED_USER_REQUEST';
export const UPDATE_ADDED_USER_SUCCESS = 'app/UserManagementPage/UPDATE_ADDED_USER_SUCCESS';
export const UPDATE_ADDED_USER_ERROR = 'app/UserManagementPage/UPDATE_ADDED_USER_ERROR';

export const FETCH_PENDING_USERS_REQUEST = 'app/UserManagementPage/FETCH_PENDING_USERS_REQUEST';
export const FETCH_PENDING_USERS_SUCCESS = 'app/UserManagementPage/FETCH_PENDING_USERS_SUCCESS';
export const FETCH_PENDING_USERS_ERROR = 'app/UserManagementPage/FETCH_PENDING_USERS_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  users: [],
  pendingUsers: [],
};

export default function userManagementPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    
    case SEND_INVITE_REQUEST:
      return { ...state };
    case SEND_INVITE_SUCCESS: {
      return {
        ...state
      };
    }
    case SEND_INVITE_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state };

    case FETCH_ALL_USERS_REQUEST:
      return {
        ...state,
      };
    case FETCH_ALL_USERS_SUCCESS:
      return {
        ...state,
        users: payload,
      };
    case FETCH_ALL_USERS_ERROR:
      return {
        ...state,
      };

    case FETCH_PENDING_USERS_REQUEST:
      return {
        ...state,
      };
    case FETCH_PENDING_USERS_SUCCESS:
      return {
        ...state,
        pendingUsers: payload,
      };
    case FETCH_PENDING_USERS_ERROR:
      return {
        ...state,
      };
      
    case UPDATE_ADDED_USER_REQUEST:
      return {
        ...state,
      };
    case UPDATE_ADDED_USER_SUCCESS:
      return {
        ...state,
        users: payload,
      };
    case UPDATE_ADDED_USER_ERROR:
      return {
        ...state,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const sendInviteRequest = () => ({ type: SEND_INVITE_REQUEST });
const sendInviteSuccess = response => ({
  type: SEND_INVITE_SUCCESS,
  payload: response,
});
const sendInviteError = e => ({
  type: SEND_INVITE_ERROR,
  error: true,
  payload: e,
});

export const fetchAllUsersUsingProfileIdRequest = params => ({ type: FETCH_ALL_USERS_REQUEST, payload: { params } });
export const fetchAllUsersUsingProfileIdSuccess = result => ({ type: FETCH_ALL_USERS_SUCCESS, payload: result });
export const fetchAllUsersUsingProfileIdError = error => ({
  type: FETCH_ALL_USERS_ERROR,
  payload: error,
  error: true,
});

export const updateAddedUserRequest = params => ({ type: UPDATE_ADDED_USER_REQUEST, payload: { params } });
export const updateAddedUserSuccess = result => ({ type: UPDATE_ADDED_USER_SUCCESS, payload: result.data });
export const updateAddedUserError = error => ({
  type: UPDATE_ADDED_USER_ERROR,
  payload: error,
  error: true,
});

export const fetchPendingUsersRequest = params => ({ type: FETCH_PENDING_USERS_REQUEST, payload: { params } });
export const fetchPendingUsersSuccess = result => ({ type: FETCH_PENDING_USERS_SUCCESS, payload: result });
export const fetchPendingUsersError = error => ({
  type: FETCH_PENDING_USERS_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  // return true;

  const promises = [
    dispatch(fetchAllUsersUsingProfileId()),
    dispatch(fetchPendingUsers())
  ];

  return Promise.all(promises)
    .then(response => {

      return response;
    })
  .catch(e => {
    dispatch(fetchOrdersOrSalesError(storableError(e)));
    throw e;
  });
};

export const sendInvite = (params, search, config) => (dispatch, getState, sdk) => {

  dispatch(sendInviteRequest()); 

  const state = getState();
  const { currentUser } = state.user;

  const listingId = currentUser?.attributes?.profile?.publicData?.firstListingId;
  const adminName = currentUser?.attributes?.profile?.displayName;
  const orgName = currentUser?.attributes?.profile.publicData?.agencyProfile?.name || currentUser?.attributes?.profile.publicData?.customerProfile?.name;
  const userType = userTypeOf(currentUser);

  params.listingId = listingId;
  params.adminName = adminName;
  params.orgName = orgName;
  params.userType = userType;

  return sendInviteAndUpdate(params)
  .then(response => {

    dispatch(sendInviteSuccess('')); 
    // dispatch(loadData());
    dispatch(fetchPendingUsers());

  }).catch((error) => {
    dispatch(sendInviteError(error)); 
    throw error;
  });
};

export const fetchAllUsersUsingProfileId = (params, search, config) => (dispatch, getState) => {
  dispatch(fetchAllUsersUsingProfileIdRequest()); 

  const state = getState();
  const { currentUser } = state.user;

  const profileListingId = currentUser?.attributes?.profile?.publicData?.firstListingId ? currentUser?.attributes?.profile?.publicData?.firstListingId : currentUser?.attributes?.profile?.publicData?.profileListingId;

  return getAddedUsersOfCompany({
    profileListingId: profileListingId,
  }).then((response) => {
    
    const users = denormalisedResponseEntities(response);

    dispatch(fetchAllUsersUsingProfileIdSuccess(users)); 
    return response;
  }).catch((error) => {
    dispatch(fetchAllUsersUsingProfileIdError(error)); 
    throw error;
  });
};

export const updateAddedUsers = (actionPayload) => async dispatch => {
  
  dispatch(updateAddedUserRequest());

  return updateOtherUsers(actionPayload)
    .then(res => {
      return Promise.all([
        dispatch(fetchAllUsersUsingProfileId()),
        dispatch(fetchPendingUsers()),
      ]).then(() => res);
      
    })
    .catch(err => {
      dispatch(updateAddedUserError(err));
      throw err;
    });
};

// Pending invitations are held by the server and given to the company's admin only. For anyone
// else - a team member on the dashboard, say - the server answers 403, which here is simply an
// empty list rather than an error. The server lists them newest first.
export const fetchPendingUsers = (params, search, config) => dispatch => {
  dispatch(fetchPendingUsersRequest());

  return fetchCompanyInvitations()
    .then(response => {
      const invitations = Array.isArray(response?.invitations) ? response.invitations : [];
      const pendingUsers = invitations.map(({ email, firstName, lastName, role }) => ({
        email,
        fname: firstName,
        lname: lastName,
        role,
      }));
      dispatch(fetchPendingUsersSuccess(pendingUsers));
      return response;
    })
    .catch(error => {
      if (error?.status === 403) {
        dispatch(fetchPendingUsersSuccess([]));
        return null;
      }
      dispatch(fetchPendingUsersError(error));
      throw error;
    });
};
